import { makeStyles } from "$components/mui-styles";
import { supplierLabel } from "$components/supplier-labels";
import { Chip, TableCell, TableRow } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import classNames from "classnames";
import { format } from "date-fns";
import { LineItemSupplier } from "elephant-web/gql";
import type { FC } from "react";
import { useIntl } from "react-intl";
import type { PriceListItem } from "../types";
import { InvoiceLineItemColor } from "./InvoiceLineItemColor";

const useStyles = makeStyles((theme) => ({
  disabledCell: {
    opacity: 0.5,
  },
  missingPriceRow: {
    backgroundColor: theme.palette.warning.light,
  },
  missingPriceChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    height: 20,
    fontSize: "0.7rem",
  },
}));

export const InvoiceLineItem: FC<{
  quantity: number;
  priceListItem: PriceListItem;
  formattedPrice: string;
  formattedTotal?: string;
  automated: boolean;
  disabled: boolean;
  missingPrice: boolean;
  incurredAt: Date;
  supplier?: LineItemSupplier | null;
}> = ({
  quantity,
  priceListItem,
  formattedPrice,
  formattedTotal,
  automated,
  disabled,
  missingPrice,
  incurredAt,
  supplier,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { name, priceListItemType } = priceListItem;

  // Single row layout for read-only view
  return (
    <TableRow
      className={classNames({
        [classes.disabledCell]: automated && disabled,
        [classes.missingPriceRow]: missingPrice && !disabled,
      })}
    >
      <TableCell>
        {incurredAt ? (
          <span title={incurredAt.toString()}>
            {format(incurredAt, "yyyy-MM-dd")}
          </span>
        ) : null}
      </TableCell>
      <TableCell>
        <InvoiceLineItemColor type={priceListItemType} />
        {name}
        {missingPrice && !disabled && (
          <Chip
            className={classes.missingPriceChip}
            icon={<WarningIcon style={{ fontSize: "0.9rem" }} />}
            label={formatMessage({
              id: "invoicing.lineItem.missingPrice",
              defaultMessage: "Price not set",
            })}
            size="small"
          />
        )}
      </TableCell>
      <TableCell>{priceListItem?.revenueDepartment}</TableCell>
      <TableCell>{supplier ? supplierLabel(supplier) : null}</TableCell>
      <TableCell>{quantity}</TableCell>
      <TableCell>{formattedPrice}</TableCell>
      {formattedTotal !== undefined && <TableCell>{formattedTotal}</TableCell>}
    </TableRow>
  );
};
